import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { usersAPI, establishmentsAPI, loginHistoryAPI } from '../../services/api';
import { getRoleName } from '../../utils/helpers';
import { USER_ROLES, SUCCESS_MESSAGES } from '../../utils/constants';
import { FiPlus, FiSearch } from 'react-icons/fi';
import { SkeletonTable } from '../../design-system';
import UserForm from '../../components/users/UserForm';
import UserTable from '../../components/users/UserTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ChangePasswordDialog from '../../components/users/ChangePasswordDialog';
import LoginHistoryDialog from '../../components/users/LoginHistoryDialog';

const UserManagement = () => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const [users, setUsers] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [changePasswordUser, setChangePasswordUser] = useState(null);
  const [loginHistoryUser, setLoginHistoryUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    loadUsers();
    loadEstablishments();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const usersData = await usersAPI.getAll();

      // If user is platform admin, fetch login counts for each user
      if (user?.role === USER_ROLES.PLATFORM_ADMIN) {
        const usersWithLoginCounts = await Promise.all(
          usersData.map(async (u) => {
            try {
              const countData = await loginHistoryAPI.getUserLoginCount(u.id);
              return { ...u, login_count: countData.login_count };
            } catch (error) {
              console.error(`Error fetching login count for user ${u.id}:`, error);
              return { ...u, login_count: 0 };
            }
          })
        );
        setUsers(usersWithLoginCounts);
      } else {
        setUsers(usersData);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los usuarios'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadEstablishments = async () => {
    try {
      const establishmentsData = await establishmentsAPI.getAll();
      setEstablishments(establishmentsData);
    } catch (error) {
      console.error('Error loading establishments:', error);
      // Don't show error notification for establishments as it's not critical
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setShowUserForm(true);
  };

  const handleDeleteUser = (userToDelete) => {
    if (userToDelete.id === user.id) {
      addNotification({
        type: 'error',
        message: 'No puedes eliminar tu propia cuenta'
      });
      return;
    }

    setDeleteConfirm(userToDelete);
  };

  const confirmDeleteUser = async () => {
    try {
      await usersAPI.delete(deleteConfirm.id);
      setUsers(prevUsers => prevUsers.filter(u => u.id !== deleteConfirm.id));
      setDeleteConfirm(null);

      addNotification({
        type: 'success',
        message: 'Usuario eliminado exitosamente'
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      addNotification({
        type: 'error',
        message: 'Error al eliminar el usuario'
      });
    }
  };

  const handleChangePassword = (userToChange) => {
    setChangePasswordUser(userToChange);
  };

  const handlePasswordChanged = async (newPassword) => {
    try {
      await usersAPI.changePassword(changePasswordUser.id, newPassword);
      setChangePasswordUser(null);

      addNotification({
        type: 'success',
        message: 'Contraseña cambiada exitosamente'
      });
    } catch (error) {
      console.error('Error changing password:', error);
      addNotification({
        type: 'error',
        message: 'Error al cambiar la contraseña'
      });
    }
  };

  const handleUserSaved = async (userData) => {
    try {
      if (selectedUser) {
        // Update existing user
        const updatedUser = await usersAPI.update(selectedUser.id, userData);
        setUsers(prevUsers =>
          prevUsers.map(u => (u.id === selectedUser.id ? updatedUser : u))
        );
        addNotification({
          type: 'success',
          message: SUCCESS_MESSAGES.USER_UPDATED
        });
      } else {
        // Create new user
        const newUser = await usersAPI.create(userData);
        setUsers(prevUsers => [...prevUsers, newUser]);
        addNotification({
          type: 'success',
          message: SUCCESS_MESSAGES.USER_CREATED
        });
      }

      setShowUserForm(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      addNotification({
        type: 'error',
        message: 'Error al guardar el usuario'
      });
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const isUserExpired = (user) => {
    if (!user.expiration_date) return false;
    return new Date(user.expiration_date) < new Date();
  };

  const getFilteredAndSortedUsers = () => {
    // First, filter users
    const filtered = users.filter(u => {
      const matchesSearch = !searchTerm ||
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesRole = !roleFilter || u.role === roleFilter;

      const matchesStatus = !statusFilter ||
        (statusFilter === 'active' && u.is_active && !isUserExpired(u)) ||
        (statusFilter === 'inactive' && !u.is_active) ||
        (statusFilter === 'expired' && u.is_active && isUserExpired(u));

      return matchesSearch && matchesRole && matchesStatus;
    });

    // Then, sort the filtered results
    return filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'role':
          aValue = a.role;
          bValue = b.role;
          break;
        case 'last_login':
          aValue = a.last_login ? new Date(a.last_login).getTime() : 0;
          bValue = b.last_login ? new Date(b.last_login).getTime() : 0;
          break;
        case 'expiration_date':
          aValue = a.expiration_date ? new Date(a.expiration_date).getTime() : Infinity;
          bValue = b.expiration_date ? new Date(b.expiration_date).getTime() : Infinity;
          break;
        case 'login_count':
          aValue = a.login_count || 0;
          bValue = b.login_count || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getAvailableRoles = () => {
    if (user?.role === USER_ROLES.PLATFORM_ADMIN) {
      return [
        { value: USER_ROLES.PLATFORM_ADMIN, label: getRoleName(USER_ROLES.PLATFORM_ADMIN) },
        { value: USER_ROLES.SALES, label: getRoleName(USER_ROLES.SALES) },
        { value: USER_ROLES.ESTABLISHMENT_ADMIN, label: getRoleName(USER_ROLES.ESTABLISHMENT_ADMIN) },
        { value: USER_ROLES.MANAGER, label: getRoleName(USER_ROLES.MANAGER) },
        { value: USER_ROLES.RECEPTIONIST, label: getRoleName(USER_ROLES.RECEPTIONIST) },
        { value: USER_ROLES.CLEANING_STAFF, label: getRoleName(USER_ROLES.CLEANING_STAFF) },
      ];
    } else if (user?.role === USER_ROLES.SALES) {
      // Ventas puede crear todos los roles excepto admin_plataforma y ventas
      return [
        { value: USER_ROLES.ESTABLISHMENT_ADMIN, label: getRoleName(USER_ROLES.ESTABLISHMENT_ADMIN) },
        { value: USER_ROLES.MANAGER, label: getRoleName(USER_ROLES.MANAGER) },
        { value: USER_ROLES.RECEPTIONIST, label: getRoleName(USER_ROLES.RECEPTIONIST) },
        { value: USER_ROLES.CLEANING_STAFF, label: getRoleName(USER_ROLES.CLEANING_STAFF) },
      ];
    } else if (user?.role === USER_ROLES.ESTABLISHMENT_ADMIN) {
      return [
        { value: USER_ROLES.ESTABLISHMENT_ADMIN, label: getRoleName(USER_ROLES.ESTABLISHMENT_ADMIN) },
        { value: USER_ROLES.MANAGER, label: getRoleName(USER_ROLES.MANAGER) },
        { value: USER_ROLES.RECEPTIONIST, label: getRoleName(USER_ROLES.RECEPTIONIST) },
        { value: USER_ROLES.CLEANING_STAFF, label: getRoleName(USER_ROLES.CLEANING_STAFF) },
      ];
    } else if (user?.role === USER_ROLES.MANAGER) {
      // Encargado puede crear recepcionistas y personal de limpieza
      return [
        { value: USER_ROLES.RECEPTIONIST, label: getRoleName(USER_ROLES.RECEPTIONIST) },
        { value: USER_ROLES.CLEANING_STAFF, label: getRoleName(USER_ROLES.CLEANING_STAFF) },
      ];
    } else {
      return [
        { value: USER_ROLES.RECEPTIONIST, label: getRoleName(USER_ROLES.RECEPTIONIST) },
        { value: USER_ROLES.CLEANING_STAFF, label: getRoleName(USER_ROLES.CLEANING_STAFF) },
      ];
    }
  };

  const filteredUsers = getFilteredAndSortedUsers();
  const availableRoles = getAvailableRoles();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Gestión de Usuarios
        </h1>
        <button
          onClick={handleCreateUser}
          className="btn btn-primary flex items-center space-x-2"
        >
          <FiPlus className="h-4 w-4" />
          <span>Nuevo Usuario</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4 flex-wrap gap-y-2">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Role filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rol
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los roles</option>
              {availableRoles.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
              <option value="expired">Caducados</option>
            </select>
          </div>

          {/* Clear filters button */}
          {(searchTerm || roleFilter || statusFilter) && (
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setRoleFilter('');
                  setStatusFilter('');
                }}
                className="px-3 py-2 mt-6 btn btn-primary flex items-center space-x-2 border border-gray-300 rounded-md text-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Limpiar filtros
              </button>
            </div>
          )}
        </div>

        {/* Results count */}
        {!isLoading && (
          <div className="mt-4">
            <p className="text-sm text-gray-600">
              Mostrando {filteredUsers.length} de {users.length} usuarios
            </p>
          </div>
        )}
      </div>

      {/* Users Table with Skeleton */}
      {isLoading ? (
        <SkeletonTable rows={10} columns={6} showActions />
      ) : (
        <UserTable
          users={filteredUsers}
          onEdit={handleEditUser}
          onDelete={handleDeleteUser}
          onChangePassword={handleChangePassword}
          onViewLoginHistory={setLoginHistoryUser}
          currentUserId={user?.id}
          currentUserRole={user?.role}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      )}

      {/* User Form Modal */}
      {showUserForm && (
        <UserForm
          user={selectedUser}
          availableRoles={availableRoles}
          availableEstablishments={establishments}
          currentUser={user}
          onSave={handleUserSaved}
          onClose={() => {
            setShowUserForm(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <ConfirmDialog
          title="Eliminar Usuario"
          message={`¿Estás seguro de que deseas eliminar al usuario "${deleteConfirm.name}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          confirmStyle="danger"
          onConfirm={confirmDeleteUser}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {/* Change Password Dialog */}
      {changePasswordUser && (
        <ChangePasswordDialog
          user={changePasswordUser}
          onSave={handlePasswordChanged}
          onClose={() => setChangePasswordUser(null)}
        />
      )}

      {/* Login History Dialog */}
      {loginHistoryUser && (
        <LoginHistoryDialog
          user={loginHistoryUser}
          onClose={() => setLoginHistoryUser(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;